import React, { useState } from "react";
import { Share2, Link2, Pencil, Loader2, Check, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const enc = encodeURIComponent;
const NETWORKS = [
  ["whatsapp", "WhatsApp", MessageCircle, (u, t) => `https://wa.me/?text=${enc(`${t} ${u}`)}`],
  ["x", "X", ({ size }) => <span style={{ width: size }} className="font-black text-center leading-none">𝕏</span>, (u, t) => `https://x.com/intent/post?url=${enc(u)}&text=${enc(t)}`],
  ["facebook", "Facebook", ({ size }) => <span style={{ width: size }} className="font-black text-center leading-none">f</span>, (u) => `https://www.facebook.com/sharer/sharer.php?u=${enc(u)}`],
  ["telegram", "Telegram", Send, (u, t) => `https://t.me/share/url?url=${enc(u)}&text=${enc(t)}`],
];

export function ShareGame({ g }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/games/${g.id}`;
  const text = `${g.name}${g.rating_count ? ` — rated ${g.rating_avg.toFixed(1)}/10` : ""} on 7 Legendary`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Could not copy link"); }
  };
  const item = "flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/10 hover:text-white";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="game-share-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 text-sm font-bold text-white/80 hover:text-white hover:border-white/30 transition-colors"><Share2 size={15} /> Share</button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-52 bg-[#0d1117] border-white/10 p-1.5" data-testid="game-share-menu">
        {NETWORKS.map(([id, label, Icon, href]) => (
          <a key={id} href={href(url, text)} target="_blank" rel="noopener noreferrer" className={item} data-testid={`game-share-${id}`}><Icon size={14} /> {label}</a>
        ))}
        <button onClick={copy} className={item} data-testid="game-share-copy">{copied ? <Check size={14} className="text-[hsl(var(--primary))]" /> : <Link2 size={14} />} Copy link</button>
        {navigator.share && <button onClick={() => navigator.share({ title: g.name, text, url }).catch(() => {})} className={item} data-testid="game-share-native">More…</button>}
      </PopoverContent>
    </Popover>
  );
}

export function EditGameInfo({ g, onSaved }) {
  const [open, setOpen] = useState(false);
  const [publisher, setPublisher] = useState(g.publisher || "");
  const [date, setDate] = useState((g.published_at || "").slice(0, 10));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const r = await api.put(`/gamehub/item/${g.id}/info`, { publisher, published_at: date }); onSaved(r.data); setOpen(false); toast.success("Game info updated"); }
    catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not save"); } finally { setBusy(false); }
  };
  const inp = "w-full px-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-sm text-white focus:outline-none focus:border-[hsl(var(--primary))] [color-scheme:dark]";
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="edit-info-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 text-xs font-bold text-white/70 hover:text-white"><Pencil size={12} /> Edit info</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0d1117] border-white/10 text-white max-w-md" data-testid="edit-info-modal">
          <DialogHeader><DialogTitle className="font-head">Game info</DialogTitle></DialogHeader>
          <label className="text-xs font-bold uppercase tracking-wider text-white/50">Publisher
            <input value={publisher} onChange={(e) => setPublisher(e.target.value)} maxLength={80} className={`${inp} mt-1.5`} data-testid="edit-info-publisher" /></label>
          <label className="text-xs font-bold uppercase tracking-wider text-white/50">Published date
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={`${inp} mt-1.5`} data-testid="edit-info-date" /></label>
          <button onClick={save} disabled={busy || !publisher.trim() || !date} data-testid="edit-info-save" className="w-full py-2.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-bold disabled:opacity-50">{busy ? <Loader2 size={15} className="animate-spin mx-auto" /> : "Save"}</button>
        </DialogContent>
      </Dialog>
    </>
  );
}
